import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Stack, Typography, IconButton } from "@mui/material";
import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useGetTagsListQuery, useTagSearchTermsWithTagsBulkMutation, useBulkRemoveClustersMutation, ClusterGridParams } from "src/context/api/tags/api";
import { useEffect, useMemo, useState } from "react";
import Iconify from "src/components/iconify";
import GenericInfiniteScrollList from "src/sections/overview/dashboards/views/generic-infinite-scroll-list";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import FormProvider, { RHFAutocomplete } from "src/components/hook-form";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import { generateColorFromString } from "../cluster-color";
import { buildBulkTagArgs } from "./bulk-tag-args";
import { removableClusters } from "../chips";

export default function ClusterToolbar({ apiRef, fullResult, filterModel, multiTaggable, rowSelectionModel, filterdSummary, refetchTags, rows, queryParams, regionScoped, ...props }: any & { filterModel: GridFilterModel, multiTaggable: boolean, rowSelectionModel: GridRowSelectionModel, filterdSummary: any, refetchTags: () => void, rows: any[], queryParams: ClusterGridParams, regionScoped: boolean }) {
    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: queryParams.workspaceId, module: queryParams.module });
    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const [tagSearchTermsWithTagsBulk] = useTagSearchTermsWithTagsBulkMutation();
    const [bulkRemoveClusters] = useBulkRemoveClustersMutation();
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
        setOpen(false);
    };
    const [openRemove, setOpenRemove] = useState(false);
    const [clustersToRemove, setClustersToRemove] = useState<any[]>([]);

    const selectedClusters = useMemo(
        () => removableClusters(rows, rowSelectionModel, queryParams.region, regionScoped),
        [rows, rowSelectionModel, queryParams.region, regionScoped]
    );

    useEffect(() => {
        if (openRemove) {
            setClustersToRemove(selectedClusters);
        }
    }, [openRemove, selectedClusters]);

    const formValuesFactory = useMemo(() => {
        const searchTerms = fullResult ? filterdSummary?.selected.map((row: any) => row.id) : rowSelectionModel.map((row: any) => ({ id: row, label: row }))
        return {
            searchTerms,
            tags: [] as { label: string }[],
        }
    }, [fullResult, filterdSummary?.selected, rowSelectionModel]);

    const methods = useForm({
        defaultValues: formValuesFactory,
    });

    useEffect(() => {
        if (tagsList?.length > 0 && tagsOptions.length === 0 || tagsList?.length != tagsOptions.length) {
            setTagsOptions(tagsList.map((tag: any) => ({ id: tag.id, label: tag.name })));
        }
    }, [tagsList]);

    useEffect(() => {
        if (rowSelectionModel.length > 0) {
            methods.setValue(
                "searchTerms",
                rowSelectionModel.map((row: any) => ({
                    id: row,
                    label: row,
                }))
            );
        } else {
            methods.setValue("searchTerms", []);
        }
    }, [rowSelectionModel]);


    const handleSubmit = (data: any) => {
        tagSearchTermsWithTagsBulk(
            buildBulkTagArgs({ fullResult, queryParams, addTags: data.tags, searchTerms: data.searchTerms })
        )
            .unwrap()
            .then(() => {
                handleClose();
                enqueueSnackbar("Search terms clustered successfully", { variant: "success" });
            })
            .catch(() => {
                enqueueSnackbar("Clustering search terms failed", { variant: "error" });
            });
    }

    const chipProps = {
        sx: {
            "&:hover": {
                backgroundColor: "rgba(145, 158, 171, 0.16)",
            },
            my: 0,
            py: 0,
            backgroundColor: "rgba(145, 158, 171, 0.16)",
            color: "rgba(94, 109, 123, 1)",
            maxHeight: "24px"
        },
        onDelete: undefined,
    }

    const handleRemoveClusters = () => {
        bulkRemoveClusters({
            workspaceId: queryParams.workspaceId,
            module: queryParams.module,
            region: queryParams.region,
            tags: clustersToRemove.map((cluster) => cluster.name),
            searchTerms: fullResult
                ? filterdSummary?.selected.map((row: any) => row.id)
                : rowSelectionModel,
        })
            .unwrap()
            .then(() => {
                enqueueSnackbar(
                    "Clusters removed successfully",
                    { variant: "success" }
                );

                setOpenRemove(false);
            })
            .catch(() => {
                enqueueSnackbar(
                    "Removing clusters failed",
                    { variant: "error" }
                );
            });
    };

    return (
        <Stack
            px="20px"
            height="40px"
            justifyContent="center"
        >
            <Stack
                className="custom-toolbar"
                direction="row"
                alignItems="center"
                justifyContent="center"
                mx={0}
                pr={0}
                gap={1}
                sx={{ height: "100%" }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ height: "100%" }}
                >
                    <CustomTooltip title={"Apply Filters"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showFilterPanel()}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="ic:round-filter-list"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>
                </Stack>
                <Stack flexGrow={1} />

                {(rowSelectionModel.length > 0 || fullResult) && (
                    <Button
                        sx={{ backgroundColor: 'rgba(145, 158, 171, 0.20)', color: "#333C47" }}
                        variant="contained"
                        onClick={() => setOpenRemove(true)}
                        size="small"
                    >
                        Remove
                    </Button>
                )}
                <Button variant={"contained"} size="small" sx={{ backgroundColor: "#181818", px: 1.5, mx: 0, my: 1 }} onClick={() => setOpen(true)}>Add cluster</Button>
            </Stack>
            <Dialog fullWidth maxWidth="sm" sx={{ padding: "24px" }} open={open} onClose={handleClose}>
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 3,
                        pr: 2,
                    }}
                >
                    <Typography
                        sx={{
                            color: "#333C47",
                            fontFamily: "Public Sans",
                            fontSize: "18px",
                            fontWeight: 500,
                            lineHeight: "28px",
                        }}
                    >
                        Add clusters
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: "#919EAB",
                            p: 0.5,
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M9 1L1 9M1 1L9 9" stroke="#5E6D7B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: "24px", }}>
                    <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleSubmit)}>
                        <RHFAutocomplete
                            multiple={true}
                            freeSolo={true}
                            name="searchTerms"
                            sx={{ mt: 2 }}
                            disabled={fullResult}
                            renderInput={(params) => <TextField label={fullResult ? "Search terms" : "Add / Paste Search terms"} placeholder={fullResult ? "Search terms" : "Add / Paste Search terms"} {...params} />}
                            renderTags={(value, getTagProps) => {
                                const tags = value.slice(0, 2).map((item: any, index: number) => (
                                    <Chip  {...getTagProps({ index })} label={item.label || item} {...chipProps} key={index} />
                                ))
                                const remainingCount = fullResult ? filterdSummary?.total_search_terms - 2 : value.length - 2;
                                if (remainingCount > 0) {
                                    return [
                                        ...tags,
                                        <Chip label={`+${remainingCount}`} {...chipProps} key="remaining" />
                                    ]
                                }
                                return tags;
                            }}
                        />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            searchPlaceholder="Type and press enter to add a cluster"
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            size="medium"
                            options={tagsOptions}
                            items={tagsOptions}
                            autoHighlight={false}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => (option.id || option as any) === (value.id || value as any)}
                        />
                        <Button type="submit" sx={{ mt: "24px", backgroundColor: "#181818" }} variant="contained" color="primary">Add</Button>
                    </FormProvider>
                </DialogContent>
            </Dialog>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={openRemove}
                onClose={() => setOpenRemove(false)}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 3,
                        pr: 2,
                    }}
                >
                    <Typography
                        sx={{
                            color: "#333C47",
                            fontFamily: "Public Sans",
                            fontSize: "18px",
                            fontWeight: 600,
                            lineHeight: "28px",
                        }}
                    >
                        Remove Clusters
                    </Typography>

                    <IconButton
                        onClick={() => setOpenRemove(false)}
                        sx={{
                            color: "#919EAB",
                            p: 0.5,
                        }}
                    >
                        <Iconify icon="mingcute:close-line" width={20} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: "24px" }}>
                    <Typography
                        sx={{
                            color: "rgba(94, 109, 123, 1)",
                            fontSize: "12px",
                            mb: 1,
                        }}
                    >
                        Clusters to remove
                    </Typography>

                    <Stack
                        sx={{
                            border: "1px solid rgba(145, 158, 171, 0.32)",
                            borderRadius: "8px",
                            minHeight: "45px",
                            p: 1,
                        }}
                        direction="row"
                        gap={1}
                        flexWrap="wrap"
                    >
                        {clustersToRemove.map((cluster: any) => (
                            <Chip
                                key={cluster.id}
                                label={cluster.name}
                                size="small"
                                variant="soft"
                                onDelete={() => {
                                    setClustersToRemove((prev) =>
                                        prev.filter((c) => c.id !== cluster.id)
                                    );
                                }}
                                sx={{
                                    backgroundColor: "#EAEAEA",
                                    color: "#333C47",
                                }}
                            />
                        ))}
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "flex-start",
                        p: "24px",
                    }}
                >
                    <Button
                        sx={{ backgroundColor: "#161C24", color: "white" }}
                        variant="contained"
                        onClick={handleRemoveClusters}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}
